import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Radio, Users, Video, Mic, MonitorPlay } from 'lucide-react';

const LiveModeSelector = ({ isOpen, onClose, onSelectMode }) => {
    const [selectedMode, setSelectedMode] = useState(null);

    const handleStart = () => {
        if (selectedMode) {
            onSelectMode(selectedMode);
            onClose();
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[500px] bg-[#1a0a0e] border-[#3d1a24] text-[#F5E3D7]">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-bold text-[#FFC857] flex items-center gap-2">
                        <Video className="w-6 h-6" />
                        Go Live
                    </DialogTitle>
                    <DialogDescription className="text-[#F5E3D7]/70">
                        Choose how you want to go live with your audience
                    </DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 py-4">
                    {/* Broadcast Mode */}
                    <div
                        className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 ${selectedMode === 'broadcast'
                                ? 'border-[#FFC857] bg-[#FFC857]/10'
                                : 'border-[#3d1a24] hover:border-[#FFC857]/50 bg-[#2e0510]/50'
                            }`}
                        onClick={() => setSelectedMode('broadcast')}
                    >
                        <div className="flex items-start gap-4">
                            <div className={`p-3 rounded-lg ${selectedMode === 'broadcast' ? 'bg-[#FFC857]' : 'bg-[#3d1a24]'}`}>
                                <Radio className={`w-6 h-6 ${selectedMode === 'broadcast' ? 'text-[#2e0510]' : 'text-[#FFC857]'}`} />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-semibold text-lg text-[#F5E3D7]">Broadcast Mode</h3>
                                <p className="text-sm text-[#F5E3D7]/60 mt-1">
                                    You stream video, viewers watch and react with emojis & chat
                                </p>
                                <div className="flex items-center gap-2 mt-3 text-xs text-[#F5E3D7]/50">
                                    <span className="flex items-center gap-1">
                                        <Video className="w-3 h-3" /> One-to-Many
                                    </span>
                                    <span>•</span>
                                    <span>Great for webinars & announcements</span>
                                </div>
                            </div>
                        </div>
                        {selectedMode === 'broadcast' && (
                            <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-[#FFC857]" />
                        )}
                    </div>

                    {/* Conference Mode */}
                    <div
                        className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 ${selectedMode === 'conference'
                                ? 'border-[#FFC857] bg-[#FFC857]/10'
                                : 'border-[#3d1a24] hover:border-[#FFC857]/50 bg-[#2e0510]/50'
                            }`}
                        onClick={() => setSelectedMode('conference')}
                    >
                        <div className="flex items-start gap-4">
                            <div className={`p-3 rounded-lg ${selectedMode === 'conference' ? 'bg-[#FFC857]' : 'bg-[#3d1a24]'}`}>
                                <Users className={`w-6 h-6 ${selectedMode === 'conference' ? 'text-[#2e0510]' : 'text-[#FFC857]'}`} />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-semibold text-lg text-[#F5E3D7]">Conference Mode</h3>
                                <p className="text-sm text-[#F5E3D7]/60 mt-1">
                                    All members can go live and join with video (Zoom-style)
                                </p>
                                <div className="flex items-center gap-2 mt-3 text-xs text-[#F5E3D7]/50">
                                    <span className="flex items-center gap-1">
                                        <Mic className="w-3 h-3" /> Many-to-Many
                                    </span>
                                    <span>•</span>
                                    <span>Great for meetings & discussions</span>
                                </div>
                            </div>
                        </div>
                        {selectedMode === 'conference' && (
                            <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-[#FFC857]" />
                        )}
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={onClose}
                        className="border-[#3d1a24] text-[#F5E3D7] hover:bg-[#3d1a24]"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleStart}
                        disabled={!selectedMode}
                        className={`${selectedMode
                                ? 'bg-[#FFC857] text-[#2e0510] hover:bg-[#FFD87A]'
                                : 'bg-[#3d1a24] text-[#F5E3D7]/50'
                            }`}
                    >
                        <MonitorPlay className="w-4 h-4 mr-2" />
                        Start Live
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default LiveModeSelector;
